import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import {
  brokers,
  integrations,
  invitations,
  members,
  userAddress,
  type GrantableRole,
  type InvitationInput,
  type InvitationIssued,
  type Member,
  type UserPersonalData,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { formatarCpf, formatarTelefone, soDigitos } from './formatoDoUsuario';

/**
 * Cadastrar, editar e visualizar usuario, como o formulario "Adicionar usuario" do
 * OnPoint e na ordem dele: Tipo de perfil, Tipo de documento (fixo em CPF), Nome do
 * usuario, CPF, Telefone, E-mail e a secao Endereco (CEP, Endereco, Numero,
 * Complemento, Bairro, UF e Cidade); Cancelar e Salvar.
 *
 * Cadastrar e um convite: a pessoa escolhe a propria senha pelo link, e ninguem que
 * administra a empresa a define. Os dados cadastrais vao no convite e chegam ao
 * vinculo no aceite. Tipo de perfil, Nome, CPF, Telefone e E-mail sao obrigatorios,
 * como no OnPoint; o endereco e opcional.
 */
export type ModoDoFormulario = 'criar' | 'editar' | 'visualizar';

interface Rascunho extends UserPersonalData {
  name: string;
  email: string;
  roleIds: string[];
  brokerId: string;
}

function rascunhoInicial(
  membro: Member | undefined,
  papeis: GrantableRole[],
): Rascunho {
  if (!membro) {
    return {
      name: '',
      email: '',
      // O perfil padrao (IsDefault no OnPoint) ja vem marcado.
      roleIds: papeis.filter((papel) => papel.is_default).map((papel) => papel.id),
      brokerId: '',
      cpf: '',
      phone: '',
      address_zip: '',
      address_street: '',
      address_number: '',
      address_complement: '',
      address_district: '',
      address_state: '',
      address_city_code: '',
    };
  }
  return {
    name: membro.name,
    email: membro.email,
    roleIds: membro.roles.map((papel) => papel.id),
    brokerId: membro.broker_id ?? '',
    cpf: formatarCpf(membro.cpf),
    phone: formatarTelefone(membro.phone),
    address_zip: membro.address_zip,
    address_street: membro.address_street,
    address_number: membro.address_number,
    address_complement: membro.address_complement,
    address_district: membro.address_district,
    address_state: membro.address_state,
    address_city_code: membro.address_city_code,
  };
}

type Erros = Partial<Record<keyof Rascunho, string | undefined>>;

function conferir(rascunho: Rascunho, modo: ModoDoFormulario): Erros {
  const erros: Erros = {};
  if (modo === 'criar' && rascunho.roleIds.length === 0) {
    erros.roleIds = 'Escolha ao menos um perfil.';
  }
  if (!rascunho.name.trim()) erros.name = 'Informe o nome do usuario.';
  if (soDigitos(rascunho.cpf).length !== 11)
    erros.cpf = 'Informe os 11 digitos do CPF.';
  const telefone = soDigitos(rascunho.phone).length;
  if (telefone < 10 || telefone > 11) erros.phone = 'Informe o DDD e o numero.';
  if (!rascunho.email.trim()) erros.email = 'Informe o e-mail.';
  const cep = soDigitos(rascunho.address_zip).length;
  if (cep !== 0 && cep !== 8) erros.address_zip = 'Informe os 8 digitos do CEP.';
  return erros;
}

export function UserFormModal({
  modo,
  membro,
  papeis,
  daCorretora,
  podeGerirPapeis,
  onClose,
  onCriado,
}: {
  modo: ModoDoFormulario;
  membro?: Member | undefined;
  papeis: GrantableRole[];
  daCorretora: boolean;
  podeGerirPapeis: boolean;
  onClose: () => void;
  onCriado?: ((emitido: InvitationIssued) => void) | undefined;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const idDoPerfil = useId();

  const [rascunho, setRascunho] = useState<Rascunho>(() =>
    rascunhoInicial(membro, papeis),
  );
  const [erros, setErros] = useState<Erros>({});
  const leitura = modo === 'visualizar';

  // A lista de perfis pode chegar depois de o formulario abrir. O perfil padrao entra
  // marcado quando ela chega, se ninguem mexeu nos perfis ainda. O ajuste acontece na
  // renderizacao, sem efeito, para nao mostrar o formulario uma vez sem a marca.
  const [padraoAplicado, setPadraoAplicado] = useState(papeis.length > 0);
  if (modo === 'criar' && !padraoAplicado && papeis.length > 0) {
    setPadraoAplicado(true);
    if (rascunho.roleIds.length === 0) {
      setRascunho((atual) => ({
        ...atual,
        roleIds: papeis.filter((papel) => papel.is_default).map((papel) => papel.id),
      }));
    }
  }

  // So o perfil atribuivel e oferecido. No editar, o que a pessoa ja tem continua na
  // lista, para que salvar nao o tire sem ninguem ter pedido.
  const oferecidos = papeis.filter(
    (papel) => papel.is_assignable !== false || rascunho.roleIds.includes(papel.id),
  );
  const escolheuPerfilDaCorretora = papeis.some(
    (papel) => papel.broker_scoped && rascunho.roleIds.includes(papel.id),
  );

  const ufs = useQuery({
    queryKey: ['catalogos', 'uf'],
    queryFn: ({ signal }) => userAddress.states(signal),
    staleTime: Infinity,
  });
  const cidades = useQuery({
    queryKey: ['catalogos', 'municipios', rascunho.address_state],
    queryFn: ({ signal }) => userAddress.cities(rascunho.address_state, signal),
    enabled: rascunho.address_state !== '',
    staleTime: Infinity,
  });
  const corretoras = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: modo === 'criar' && !daCorretora && can('corretores.corretor.view'),
  });

  function campo<K extends keyof Rascunho>(chave: K, valor: Rascunho[K]) {
    setRascunho((atual) => ({ ...atual, [chave]: valor }));
    setErros((atual) => ({ ...atual, [chave]: undefined }));
  }

  function alternarPerfil(roleId: string) {
    campo(
      'roleIds',
      rascunho.roleIds.includes(roleId)
        ? rascunho.roleIds.filter((id) => id !== roleId)
        : [...rascunho.roleIds, roleId],
    );
  }

  // Com os 8 digitos, o CEP preenche o endereco pela consulta de CEP. A consulta e
  // conveniencia: sem ela ligada, a pessoa digita o endereco.
  async function consultarCep() {
    const cep = soDigitos(rascunho.address_zip);
    if (cep.length !== 8 || leitura) return;
    try {
      const endereco = await integrations.lookupAddress(cep);
      setRascunho((atual) => ({
        ...atual,
        address_street: atual.address_street || endereco.street,
        address_district: atual.address_district || endereco.district,
        address_state: endereco.state || atual.address_state,
        address_city_code:
          endereco.state === atual.address_state ? atual.address_city_code : '',
      }));
    } catch {
      // Sem consulta de CEP, o endereco continua digitado a mao.
    }
  }

  const dadosCadastrais = (): UserPersonalData => ({
    cpf: soDigitos(rascunho.cpf),
    phone: soDigitos(rascunho.phone),
    address_zip: soDigitos(rascunho.address_zip),
    address_street: rascunho.address_street.trim(),
    address_number: rascunho.address_number.trim(),
    address_complement: rascunho.address_complement.trim(),
    address_district: rascunho.address_district.trim(),
    address_state: rascunho.address_state,
    address_city_code: rascunho.address_city_code,
  });

  const salvar = useMutation({
    mutationFn: async () => {
      if (modo === 'criar') {
        const entrada: InvitationInput = {
          email: rascunho.email.trim(),
          name: rascunho.name.trim(),
          role_ids: rascunho.roleIds,
          ...dadosCadastrais(),
          // A seguradora escolhe a corretora do perfil com escopo. A corretora nao
          // escolhe: o servidor prende o convite a corretora da sessao.
          ...(!daCorretora && escolheuPerfilDaCorretora && rascunho.brokerId
            ? { broker_id: rascunho.brokerId }
            : {}),
        };
        return { emitido: await invitations.create(entrada) };
      }
      const atual = membro!;
      await members.update(atual.id, {
        name: rascunho.name.trim(),
        ...dadosCadastrais(),
      });
      const mudouPerfil =
        rascunho.roleIds.length !== atual.roles.length ||
        rascunho.roleIds.some((id) => !atual.roles.some((papel) => papel.id === id));
      if (podeGerirPapeis && mudouPerfil) {
        await members.setRoles(atual.id, rascunho.roleIds);
      }
      return { emitido: null };
    },
    onSuccess: ({ emitido }) => {
      void queryClient.invalidateQueries({ queryKey: ['members'] });
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      if (emitido) {
        onCriado?.(emitido);
        return;
      }
      toast.success('Usuario atualizado.');
      onClose();
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const encontrados = conferir(rascunho, modo);
    setErros(encontrados);
    if (Object.values(encontrados).some(Boolean)) return;
    salvar.mutate();
  }

  const titulo =
    modo === 'criar'
      ? 'Adicionar usuario'
      : modo === 'editar'
        ? 'Editar usuario'
        : 'Visualizar usuario';

  const opcoesDeUf = (ufs.data ?? []).map((uf) => ({ value: uf.code, label: uf.name }));
  const opcoesDeCidade = (cidades.data ?? []).map((cidade) => ({
    value: cidade.ibge_code,
    label: cidade.name,
  }));

  return (
    <Modal
      open
      onClose={onClose}
      title={titulo}
      {...(membro ? { description: membro.email } : {})}
      size="md"
    >
      <form onSubmit={enviar} className="flex flex-col gap-5" noValidate>
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}

        {modo === 'visualizar' && membro ? <ResumoDoVinculo membro={membro} /> : null}

        <fieldset
          className="flex flex-col gap-2"
          aria-describedby={`${idDoPerfil}-erro`}
        >
          <FieldLabel
            htmlFor={`${idDoPerfil}-0`}
            label="Tipo de perfil"
            className="text-xs font-semibold text-ink-700"
          />
          {oferecidos.length === 0 && !leitura ? (
            <Alert tone="warning" title="Perfis indisponiveis">
              Nao foi possivel carregar os perfis. Sem perfil a pessoa entraria sem
              conseguir fazer nada.
            </Alert>
          ) : null}
          {leitura || (modo === 'editar' && !podeGerirPapeis) ? (
            <p className="text-sm text-ink-800">
              {(membro?.roles ?? []).map((papel) => papel.name).join(', ') ||
                'Sem perfil'}
            </p>
          ) : (
            <ul className="flex max-h-48 flex-col divide-y divide-line overflow-y-auto rounded-md border border-line">
              {oferecidos.map((papel, indice) => (
                <li key={papel.id}>
                  <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2 hover:bg-ink-50">
                    <input
                      id={`${idDoPerfil}-${indice}`}
                      type="checkbox"
                      className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                      checked={rascunho.roleIds.includes(papel.id)}
                      onChange={() => alternarPerfil(papel.id)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-ink-800">
                        {papel.name}
                      </span>
                      <span className="block text-2xs text-ink-500">
                        {papel.grants_all
                          ? 'Concede todas as permissoes do catalogo.'
                          : papel.description}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {erros.roleIds ? (
            <p id={`${idDoPerfil}-erro`} role="alert" className="text-2xs text-red-700">
              {erros.roleIds}
            </p>
          ) : null}
        </fieldset>

        {modo === 'criar' && !daCorretora && escolheuPerfilDaCorretora ? (
          <Select
            label="Corretora representada"
            placeholder="Nenhuma corretora"
            value={rascunho.brokerId}
            options={(corretoras.data?.items ?? []).map((corretora) => ({
              value: corretora.id,
              label: corretora.legal_name,
            }))}
            hint="Sem corretora, a pessoa com perfil da corretora nao enxerga nada."
            onChange={(evento) => campo('brokerId', evento.target.value)}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Tipo de documento"
            value="cpf"
            disabled
            options={[{ value: 'cpf', label: 'CPF' }]}
            onChange={() => undefined}
          />
          <Field
            label="Nome do usuario"
            required
            maxLength={120}
            placeholder="Digite o nome do usuario"
            readOnly={leitura}
            value={rascunho.name}
            error={erros.name}
            onChange={(evento) => campo('name', evento.target.value)}
          />
          <Field
            label="CPF"
            required
            inputMode="numeric"
            placeholder="Digite o CPF"
            readOnly={leitura}
            value={rascunho.cpf}
            error={erros.cpf}
            onChange={(evento) => campo('cpf', evento.target.value)}
            onBlur={() => campo('cpf', formatarCpf(soDigitos(rascunho.cpf)))}
          />
          <Field
            label="Telefone"
            required
            inputMode="tel"
            placeholder="Digite o telefone"
            readOnly={leitura}
            value={rascunho.phone}
            error={erros.phone}
            onChange={(evento) => campo('phone', evento.target.value)}
            onBlur={() => campo('phone', formatarTelefone(soDigitos(rascunho.phone)))}
          />
          <Field
            label="E-mail"
            type="email"
            required
            placeholder="Digite o e-mail"
            readOnly={leitura || modo === 'editar'}
            value={rascunho.email}
            error={erros.email}
            hint={
              modo === 'editar'
                ? 'O e-mail e o acesso da pessoa e nao muda aqui.'
                : undefined
            }
            onChange={(evento) => campo('email', evento.target.value)}
          />
        </div>

        <fieldset className="flex flex-col gap-3 border-t border-line pt-4">
          <legend className="text-sm font-semibold text-ink-900">Endereco</legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="CEP"
              inputMode="numeric"
              placeholder="Digite o CEP"
              readOnly={leitura}
              value={rascunho.address_zip}
              error={erros.address_zip}
              onChange={(evento) => campo('address_zip', evento.target.value)}
              onBlur={() => void consultarCep()}
            />
            <div className="sm:col-span-2">
              <Field
                label="Endereco"
                maxLength={200}
                placeholder="Digite o endereco"
                readOnly={leitura}
                value={rascunho.address_street}
                onChange={(evento) => campo('address_street', evento.target.value)}
              />
            </div>
            <Field
              label="Numero"
              maxLength={20}
              placeholder="Digite o numero"
              readOnly={leitura}
              value={rascunho.address_number}
              onChange={(evento) => campo('address_number', evento.target.value)}
            />
            <Field
              label="Complemento"
              maxLength={120}
              placeholder="Digite o complemento"
              readOnly={leitura}
              value={rascunho.address_complement}
              onChange={(evento) => campo('address_complement', evento.target.value)}
            />
            <Field
              label="Bairro"
              maxLength={120}
              placeholder="Digite o bairro"
              readOnly={leitura}
              value={rascunho.address_district}
              onChange={(evento) => campo('address_district', evento.target.value)}
            />
            <Select
              label="UF"
              placeholder="Selecione a UF"
              disabled={leitura}
              value={rascunho.address_state}
              options={opcoesDeUf}
              onChange={(evento) => {
                setRascunho((atual) => ({
                  ...atual,
                  address_state: evento.target.value,
                  address_city_code: '',
                }));
              }}
            />
            <div className="sm:col-span-2">
              <Select
                label="Cidade"
                placeholder={
                  rascunho.address_state
                    ? 'Selecione a cidade'
                    : 'Escolha a UF primeiro'
                }
                disabled={leitura || rascunho.address_state === ''}
                value={rascunho.address_city_code}
                options={opcoesDeCidade}
                onChange={(evento) => campo('address_city_code', evento.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            {leitura ? 'Fechar' : 'Cancelar'}
          </Button>
          {leitura ? null : (
            <Button type="submit" loading={salvar.isPending}>
              Salvar
            </Button>
          )}
        </div>

        {modo === 'criar' ? (
          <p className="text-2xs text-ink-500">
            A pessoa recebe um convite e escolhe a propria senha pelo link. Ninguem que
            administra a empresa a define nem consegue enxerga-la.
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

/**
 * Situacao e datas do vinculo, que a API ja devolvia e a tela nao mostrava (defeito
 * 4 de "Usuarios, sessao, perfis e convites").
 */
function ResumoDoVinculo({ membro }: { membro: Member }) {
  return (
    <dl className="grid grid-cols-2 gap-3 rounded-md border border-line bg-surface-sunken p-3 text-xs">
      <div>
        <dt className="text-ink-500">Situacao</dt>
        <dd className="mt-1">
          <StatusBadge kind="vinculo" value={membro.status} />
        </dd>
      </div>
      <div>
        <dt className="text-ink-500">Ultimo acesso</dt>
        <dd className="mt-1 text-ink-800">
          {membro.last_login_at
            ? formatDateTime(membro.last_login_at)
            : 'nunca acessou'}
        </dd>
      </div>
      <div>
        <dt className="text-ink-500">Vinculo criado em</dt>
        <dd className="mt-1 text-ink-800">{formatDateTime(membro.created_at)}</dd>
      </div>
      {membro.suspended_at ? (
        <div>
          <dt className="text-ink-500">
            {membro.status === 'inactive' ? 'Inativado em' : 'Suspenso em'}
          </dt>
          <dd className="mt-1 text-ink-800">
            {formatDateTime(membro.suspended_at)}
            {membro.suspend_reason ? ` (${membro.suspend_reason})` : ''}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
